'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import classNames from 'classnames';
import { FC, useCallback } from 'react';
import { ApprovalDegree } from '../../types/approvalOdds';
import { MaterialIcon } from '../MaterialIcon';

export type ApprovalOddsLabels = {
  high: string;
  medium: string;
  low: string;
  unlock: string;
};

type ApprovalOddsBadgeProps = {
  degree: ApprovalDegree | undefined;
  isLocked: boolean;
  isFresh: boolean;
  labels: ApprovalOddsLabels;
  onUnlockClick?: () => void;
  onClick?: () => void;
};

type KnownDegree = Exclude<ApprovalDegree, 'UNKNOWN'>;

const BADGE_STYLES: Record<ApprovalDegree, string> = {
  HIGH: 'bg-highlights/green-light text-highlights/green-dark',
  MEDIUM: 'bg-highlights/orange-light text-highlights/yellow-dark',
  LOW: 'bg-highlights/red-light text-highlights/red-dark',
  UNKNOWN: 'bg-fill/alternative-2 text-[#757575]',
};

// UNKNOWN has no dot, so it is intentionally absent here.
const DOT_STYLES: Record<KnownDegree, string> = {
  HIGH: 'bg-highlights/green-dark',
  MEDIUM: 'bg-highlights/yellow-dark',
  LOW: 'bg-highlights/red-dark',
};

const LABEL_KEYS: Record<KnownDegree, keyof ApprovalOddsLabels> = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

export const ApprovalOddsBadge: FC<ApprovalOddsBadgeProps> = ({
  degree = 'UNKNOWN',
  isLocked,
  isFresh,
  labels,
  onUnlockClick,
  onClick,
}) => {
  const onUnlockClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onUnlockClick) {
        e.preventDefault();
        e.stopPropagation();

        onUnlockClick();

        return false;
      }
    },
    [onUnlockClick],
  );

  const onClickHandler = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (onClick) {
        e.preventDefault();
        e.stopPropagation();

        onClick();

        return false;
      }
    },
    [onClick],
  );

  const onKeyDownHandler = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) return;
        onClick();
      }
    },
    [onClick],
  );

  if (isLocked) {
    return (
      <button
        type="button"
        onClick={onUnlockClickHandler}
        className="flex items-center justify-center gap-spacing/gap/soft-4px rounded-radius/tooltip bg-fill/alternative-2 px-spacing/padding/soft-8px py-spacing/gap/soft-6px text-text/secondary-lighter font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell"
      >
        <MaterialIcon icon="lock" px={16} aria-hidden />
        <span>{labels.unlock}</span>
      </button>
    );
  }

  // UNKNOWN (also the default when no degree is provided) renders with the same pill
  // markup as the other degrees, but keeps the "-" glyph as its label (there is no
  // degree word for the no-odds state).
  const label = degree === 'UNKNOWN' ? '-' : labels[LABEL_KEYS[degree]];
  const isInteractive = !isLocked && !!onClick;

  return (
    <div className="flex min-h-6 items-center">
      <span
        className={classNames(
          'inline-flex rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/gap/soft-6px text-font-size/table-cell-primary font-font-weight/table-cell-strong leading-line-height/table-cell-primary justify-center items-center gap-1',
          BADGE_STYLES[degree],
          { 'opacity-50': !isFresh },
          { 'cursor-pointer': isInteractive },
        )}
        onClick={isInteractive ? onClickHandler : undefined}
        onKeyDown={isInteractive ? onKeyDownHandler : undefined}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
      >
        {degree !== 'UNKNOWN' && (
          <span
            className={classNames(
              'size-2.5 rounded-full inline-block',
              DOT_STYLES[degree],
            )}
          />
        )}
        <span>{label}</span>
      </span>
    </div>
  );
};
